"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from "react";
import type { CreateWaveConfig, CreateWaveOutcomeType, TimeWeightedVotingSettings } from "@/types/waves.types";
import { CreateWaveGroupConfigType, CreateWaveStep } from "@/types/waves.types";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { Time } from "@/helpers/time";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
import { ApiWaveCreditType } from "@/generated/models/ApiWaveCreditType";
import type { ApiWaveCreditNft } from "@/generated/models/ApiWaveCreditNft";
import type { Period } from "../types/period";
import type { CREATE_WAVE_VALIDATION_ERROR } from "@/helpers/waves/create-wave.validation";
import { getCreateWaveValidationErrors } from "@/helpers/waves/create-wave.validation";
import { assertUnreachable } from "@/helpers/AllowlistToolHelpers";
import { useMemeCardCount } from "./useMemeCardCount";
interface EndDateConfig {
  time: number | null;
  period: Period | null;
}
export function useWaveConfig() {
  const $ = _c(50);
  const initialType = ApiWaveType.Chat;
  const initialStep = CreateWaveStep.OVERVIEW;
  const getInitialConfig = _temp;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getInitialConfig({
      type: initialType
    });
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [config, setConfig] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      time: null,
      period: null
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [endDateConfig, setEndDateConfig] = useState(t1);
  const [step, setStep] = useState(initialStep);
  const [selectedOutcomeType, setSelectedOutcomeType] = useState(null);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [errors, setErrors] = useState(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [groupsCache, setGroupsCache] = useState(t3);
  const shouldLoadMemeCount = config.voting.type === ApiWaveCreditType.CardSetTdh;
  let t4;
  if ($[4] !== shouldLoadMemeCount) {
    t4 = {
      enabled: shouldLoadMemeCount
    };
    $[4] = shouldLoadMemeCount;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const memeCountQuery = useMemeCardCount(t4);
  const memeCount = shouldLoadMemeCount && !memeCountQuery.isError ? memeCountQuery.data ?? null : null;
  let t5;
  bb0: {
    if (config.voting.creditNftMemeCount === memeCount) {
      t5 = config;
      break bb0;
    }
    let t6;
    if ($[6] !== config.voting || $[7] !== memeCount) {
      t6 = {
        ...config.voting,
        creditNftMemeCount: memeCount
      };
      $[6] = config.voting;
      $[7] = memeCount;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== config || $[10] !== t6) {
      t7 = {
        ...config,
        voting: t6
      };
      $[9] = config;
      $[10] = t6;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    t5 = t7;
  }
  const effectiveConfig = t5;
  let t6;
  let t7;
  if ($[12] !== config.dates.endDate) {
    t6 = () => {
      if (config.dates.endDate === null) {
        setEndDateConfig({
          time: null,
          period: null
        });
      }
    };
    t7 = [config.dates.endDate];
    $[12] = config.dates.endDate;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setErrors([]);
    };
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== config) {
    t9 = [config];
    $[16] = config;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  useEffect(t8, t9);
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = overview => {
      setEndDateConfig({
        time: null,
        period: null
      });
      setConfig(() => ({
        ...getInitialConfig({
          type: overview.type
        }),
        overview
      }));
    };
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const setOverview = t10;
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = dates => {
      setConfig(prev => ({
        ...prev,
        dates
      }));
    };
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const setDates = t11;
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = drops => {
      setConfig(prev_0 => ({
        ...prev_0,
        drops
      }));
    };
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const setDrops = t12;
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = adminCanDeleteDrops => {
      setConfig(prev_1 => ({
        ...prev_1,
        drops: {
          ...prev_1.drops,
          adminCanDeleteDrops
        }
      }));
    };
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const setDropsAdminCanDelete = t13;
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = outcomes => {
      setConfig(prev_2 => ({
        ...prev_2,
        outcomes
      }));
    };
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const setOutcomes = t14;
  let t15;
  if ($[23] !== effectiveConfig || $[24] !== step) {
    t15 = t16 => {
      const {
        step: newStep,
        direction
      } = t16;
      if (direction === "forward") {
        const newErrors = getCreateWaveValidationErrors({
          config: effectiveConfig,
          step
        });
        if (newErrors.length) {
          setErrors(newErrors);
          return;
        }
      }
      setErrors([]);
      setSelectedOutcomeType(null);
      setStep(newStep);
    };
    $[23] = effectiveConfig;
    $[24] = step;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  const onStep = t15;
  let t16;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = outcomeType => {
      setSelectedOutcomeType(outcomeType);
      setErrors([]);
    };
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const onOutcomeTypeChange = t16;
  let t17;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = t18 => {
      const {
        group,
        groupType
      } = t18;
      if (group) {
        setGroupsCache(prev_3 => ({
          ...prev_3,
          [group.id]: group
        }));
      }
      bb55: switch (groupType) {
        case CreateWaveGroupConfigType.CAN_VIEW:
          {
            setConfig(prev_8 => ({
              ...prev_8,
              groups: {
                ...prev_8.groups,
                canView: group?.id ?? null
              }
            }));
            break bb55;
          }
        case CreateWaveGroupConfigType.CAN_DROP:
          {
            setConfig(prev_7 => ({
              ...prev_7,
              groups: {
                ...prev_7.groups,
                canDrop: group?.id ?? null
              }
            }));
            break bb55;
          }
        case CreateWaveGroupConfigType.CAN_VOTE:
          {
            setConfig(prev_6 => ({
              ...prev_6,
              groups: {
                ...prev_6.groups,
                canVote: group?.id ?? null
              }
            }));
            break bb55;
          }
        case CreateWaveGroupConfigType.CAN_CHAT:
          {
            setConfig(prev_5 => ({
              ...prev_5,
              groups: {
                ...prev_5.groups,
                canChat: group?.id ?? null
              }
            }));
            break bb55;
          }
        case CreateWaveGroupConfigType.ADMIN:
          {
            setConfig(prev_4 => ({
              ...prev_4,
              groups: {
                ...prev_4.groups,
                admin: group?.id ?? null
              }
            }));
            break bb55;
          }
        default:
          {
            assertUnreachable(groupType);
          }
      }
    };
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  const onGroupSelect = t17;
  let t18;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = type_0 => {
      setConfig(prev_9 => ({
        ...prev_9,
        voting: {
          type: type_0,
          category: null,
          profileId: null,
          creditNfts: type_0 === ApiWaveCreditType.CardSetTdh ? prev_9.voting.creditNfts : [],
          creditNftMemeCount: null,
          allowNegativeVotes: prev_9.voting.allowNegativeVotes,
          maxVotesPerIdentityPerDrop: prev_9.voting.maxVotesPerIdentityPerDrop,
          winningThreshold: prev_9.voting.winningThreshold,
          timeWeighted: prev_9.voting.timeWeighted
        }
      }));
    };
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  const onVotingTypeChange = t18;
  let t19;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = timeWeighted => {
      setConfig(prev_10 => ({
        ...prev_10,
        approval: prev_10.overview.type === ApiWaveType.Approve && timeWeighted.enabled ? {
          ...prev_10.approval,
          thresholdTimeMs: null
        } : prev_10.approval,
        voting: {
          ...prev_10.voting,
          timeWeighted
        }
      }));
    };
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  const onTimeWeightedVotingChange = t19;
  let t20;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = enabled => {
      setConfig(prev_11 => ({
        ...prev_11,
        chat: {
          enabled
        }
      }));
    };
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  const onChatEnabledChange = t20;
  let t21;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = category => {
      setConfig(prev_12 => ({
        ...prev_12,
        voting: {
          ...prev_12.voting,
          category
        }
      }));
    };
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  const onCategoryChange = t21;
  let t22;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = profileId => {
      setConfig(prev_13 => ({
        ...prev_13,
        voting: {
          ...prev_13.voting,
          profileId
        }
      }));
    };
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  const onProfileIdChange = t22;
  let t23;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = creditNfts => {
      setConfig(prev_14 => ({
        ...prev_14,
        voting: {
          ...prev_14.voting,
          creditNfts
        }
      }));
    };
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  const onCreditNftsChange = t23;
  let t24;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = maxVotesPerIdentityPerDrop => {
      setConfig(prev_15 => ({
        ...prev_15,
        voting: {
          ...prev_15.voting,
          maxVotesPerIdentityPerDrop
        }
      }));
    };
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  const onMaxVotesPerIdentityPerDropChange = t24;
  let t25;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = allowNegativeVotes => {
      setConfig(prev_16 => ({
        ...prev_16,
        voting: {
          ...prev_16.voting,
          allowNegativeVotes
        }
      }));
    };
    $[35] = t25;
  } else {
    t25 = $[35];
  }
  const onAllowNegativeVotesChange = t25;
  let t26;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = winningThreshold => {
      setConfig(prev_17 => ({
        ...prev_17,
        voting: {
          ...prev_17.voting,
          winningThreshold
        }
      }));
    };
    $[36] = t26;
  } else {
    t26 = $[36];
  }
  const onWinningThresholdChange = t26;
  let t27;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = threshold => {
      setConfig(prev_18 => ({
        ...prev_18,
        approval: {
          ...prev_18.approval,
          threshold
        }
      }));
    };
    $[37] = t27;
  } else {
    t27 = $[37];
  }
  const onThresholdChange = t27;
  let t28;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = thresholdTimeMs => {
      setConfig(prev_19 => ({
        ...prev_19,
        voting: {
          ...prev_19.voting,
          timeWeighted: prev_19.overview.type === ApiWaveType.Approve && thresholdTimeMs !== null ? {
            ...prev_19.voting.timeWeighted,
            enabled: false
          } : prev_19.voting.timeWeighted
        },
        approval: {
          ...prev_19.approval,
          thresholdTimeMs
        }
      }));
    };
    $[38] = t28;
  } else {
    t28 = $[38];
  }
  const onThresholdTimeChange = t28;
  let t29;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = maxWinners => {
      setConfig(prev_20 => ({
        ...prev_20,
        approval: {
          ...prev_20.approval,
          maxWinners
        }
      }));
    };
    $[39] = t29;
  } else {
    t29 = $[39];
  }
  const onApprovalMaxWinnersChange = t29;
  const t30 = shouldLoadMemeCount && memeCountQuery.isLoading;
  const t31 = shouldLoadMemeCount && memeCountQuery.isError;
  let t32;
  if ($[40] !== effectiveConfig || $[41] !== endDateConfig || $[42] !== errors || $[43] !== groupsCache || $[44] !== onStep || $[45] !== selectedOutcomeType || $[46] !== step || $[47] !== t30 || $[48] !== t31) {
    t32 = {
      config: effectiveConfig,
      setConfig,
      endDateConfig,
      setEndDateConfig,
      step,
      selectedOutcomeType,
      errors,
      groupsCache,
      isMemeCountLoading: t30,
      isMemeCountError: t31,
      setOverview,
      setDates,
      setDrops,
      setDropsAdminCanDelete,
      setOutcomes,
      onStep,
      onOutcomeTypeChange,
      onGroupSelect,
      onVotingTypeChange,
      onCategoryChange,
      onProfileIdChange,
      onCreditNftsChange,
      onMaxVotesPerIdentityPerDropChange,
      onAllowNegativeVotesChange,
      onTimeWeightedVotingChange,
      onWinningThresholdChange,
      onThresholdChange,
      onThresholdTimeChange,
      onApprovalMaxWinnersChange,
      onChatEnabledChange
    };
    $[40] = effectiveConfig;
    $[41] = endDateConfig;
    $[42] = errors;
    $[43] = groupsCache;
    $[44] = onStep;
    $[45] = selectedOutcomeType;
    $[46] = step;
    $[47] = t30;
    $[48] = t31;
    $[49] = t32;
  } else {
    t32 = $[49];
  }
  return t32;
}
function _temp(t0) {
  const {
    type
  } = t0;
  const now = Time.currentMillis();
  return {
    overview: {
      type,
      name: "",
      image: null
    },
    groups: {
      canView: null,
      canDrop: null,
      canVote: null,
      canChat: null,
      admin: null
    },
    chat: {
      enabled: true
    },
    dates: {
      submissionStartDate: now,
      votingStartDate: now,
      endDate: type === ApiWaveType.Rank ? now : null,
      firstDecisionTime: now,
      subsequentDecisions: [],
      isRolling: false
    },
    drops: {
      noOfApplicationsAllowedPerParticipant: null,
      requiredTypes: [],
      requiredMetadata: [],
      submissionStrategy: null,
      terms: null,
      signatureRequired: false,
      adminCanDeleteDrops: true
    },
    voting: {
      type: ApiWaveCreditType.TdhPlusXtdh,
      category: null,
      profileId: null,
      creditNfts: [],
      creditNftMemeCount: null,
      allowNegativeVotes: true,
      maxVotesPerIdentityPerDrop: null,
      winningThreshold: null,
      timeWeighted: {
        enabled: false,
        averagingInterval: 24,
        averagingIntervalUnit: "hours"
      }
    },
    outcomes: [],
    approval: {
      threshold: null,
      thresholdTimeMs: null,
      maxWinners: null
    }
  };
}